<template>
  <article
    class="ai-evidence"
    :class="[`is-${card.kind || 'text'}`, { 'is-focused': focused }]"
    :data-eid="item.id"
  >
    <header class="ai-evidence__head">
      <span
        v-if="number"
        class="ai-evidence__number"
      >
        {{ number }}
      </span>
      <span
        v-if="iconUrl"
        class="ai-evidence__source-icon"
        :style="{ '--icon': `url(${iconUrl})` }"
        aria-hidden="true"
      />
      <ai-icon
        v-else
        :name="fallbackIcon"
        :size="16"
        class="ai-evidence__fallback-icon"
      />
      <span class="ai-evidence__source">{{ sourceLabel }}</span>
      <span
        v-if="flagLabel"
        class="ai-evidence__flag ai-tone"
        :class="card.flag >= 2 ? 'is-danger' : 'is-warning'"
      >
        {{ flagLabel }}
      </span>
      <span class="ai-evidence__time">{{ time }}</span>
    </header>

    <div
      v-if="item.computerName"
      class="ai-evidence__computer"
    >
      <ai-icon
        name="computer"
        :size="14"
      />
      {{ item.computerName }}
    </div>

    <div
      v-if="hasParties"
      class="ai-evidence__parties"
    >
      <span
        v-if="card.channel"
        class="ai-evidence__channel"
      >
        {{ card.channel }}
      </span>
      <span v-if="card.actor">{{ card.actor }}</span>
      <span
        v-if="card.direction"
        class="ai-evidence__direction"
        :class="card.direction === 'in' ? 'is-in' : 'is-out'"
        :title="card.direction === 'in' ? 'Входящее' : 'Исходящее'"
      >
        <ai-icon
          :name="card.direction === 'in' ? 'arrowIn' : 'arrowOut'"
          :size="14"
        />
      </span>
      <span v-if="card.counterpart">{{ card.counterpart }}</span>
    </div>

    <div
      v-if="card.title"
      class="ai-evidence__title"
    >
      {{ card.title }}
    </div>
    <div
      v-if="card.action"
      class="ai-evidence__action"
    >
      {{ card.action }}
    </div>

    <template v-if="card.text">
      <span
        v-if="textLabel"
        class="ai-evidence__label"
      >
        {{ textLabel }}
      </span>
      <p
        class="ai-evidence__text"
        :class="{ 'is-clamped': !showFull && isLong }"
      >
        {{ card.text }}
      </p>
      <button
        v-if="isLong"
        type="button"
        class="ai-link-button"
        @click="showFull = !showFull"
      >
        {{ showFull ? 'Свернуть' : 'Показать полностью' }}
      </button>
    </template>

    <ul
      v-if="card.attachments && card.attachments.length"
      class="ai-evidence__files"
    >
      <li
        v-for="(file, index) in card.attachments"
        :key="index"
      >
        <ai-icon
          name="file"
          :size="14"
        />
        <span class="ai-evidence__file-name">{{ file.name }}</span>
        <span
          v-if="file.size"
          class="ai-evidence__muted"
        >
          {{ file.size }}
        </span>
      </li>
    </ul>

    <dl
      v-if="card.fields && card.fields.length"
      class="ai-evidence__fields"
    >
      <template v-for="(field, index) in card.fields">
        <dt :key="`l${index}`">
          {{ field.label }}
        </dt>
        <dd :key="`v${index}`">
          {{ field.value }}
        </dd>
      </template>
    </dl>

    <!-- A file read by the assistant: the record (or the file of the user) it belongs to opens in the panel. -->
    <div
      v-if="isFile && card.parent"
      class="ai-evidence__parent"
    >
      <span>{{ parentLabel }}</span>
      <button
        type="button"
        class="ai-evidence__ref"
        :title="fromUpload ? 'Показать файл' : 'Показать запись'"
        @click="$emit('open', card.parent)"
      >
        {{ card.parent }}
      </button>
    </div>

    <!-- Expanded, the same block becomes a fixed overlay over the page with the whole table. -->
    <div
      v-if="card.kind === 'report' && columns.length"
      class="ai-evidence__report"
      :class="{ 'is-open': tableOpen }"
      @click="onReportClick"
      @keydown.esc="onReportEscape"
    >
      <div
        ref="report"
        class="ai-evidence__report-box"
        :role="tableOpen ? 'dialog' : null"
        :aria-modal="tableOpen ? 'true' : null"
        :aria-label="tableOpen ? reportTitle : null"
        :tabindex="tableOpen ? -1 : null"
      >
        <header
          v-if="tableOpen"
          class="ai-evidence__report-head"
        >
          <span class="ai-evidence__report-title">{{ reportTitle }}</span>
          <span class="ai-evidence__muted">{{ rowsLabel }}</span>
          <button
            type="button"
            class="ai-button is-small is-ghost"
            title="Свернуть (Esc)"
            @click="closeTable"
          >
            <ai-icon
              name="collapse"
              :size="14"
            />
            Свернуть
          </button>
        </header>
        <div class="ai-evidence__table-wrap">
          <table class="ai-evidence__table">
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column"
                >
                  <div class="ai-evidence__cell">
                    {{ column }}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in visibleRows"
                :key="index"
              >
                <td
                  v-for="(value, cell) in row"
                  :key="cell"
                >
                  <div class="ai-evidence__cell">
                    {{ value }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="!tableOpen"
          class="ai-evidence__table-actions"
        >
          <button
            ref="expand"
            type="button"
            class="ai-button is-small is-ghost"
            @click="openTable"
          >
            <ai-icon
              name="expand"
              :size="14"
            />
            Развернуть таблицу
          </button>
          <button
            v-if="rows.length > rowLimit"
            type="button"
            class="ai-button is-small is-ghost"
            @click="allRows = !allRows"
          >
            {{ allRows ? `Показать первые ${rowLimit}` : `Показать все строки (${rows.length})` }}
          </button>
        </div>
      </div>
    </div>
    <p
      v-else-if="card.kind === 'report'"
      class="ai-evidence__muted"
    >
      В отчёте нет строк за этот период.
    </p>

    <footer
      v-if="links.length || files.length"
      class="ai-evidence__links"
    >
      <button
        v-for="(file, index) in files"
        :key="`file${index}`"
        type="button"
        class="ai-button is-small is-ghost"
        :title="file.name"
        :disabled="opening === index"
        @click="open(file, index)"
      >
        <span
          v-if="opening === index"
          class="ai-spinner ai-spinner--small"
        />
        <ai-icon
          v-else
          name="file"
          :size="14"
        />
        {{ files.length > 1 ? `${file.label}: ${file.name}` : file.label }}
      </button>
      <router-link
        v-for="link in links"
        :key="link.label"
        :to="link.to"
        target="_blank"
        class="ai-button is-small is-ghost"
        :title="link.title"
      >
        <ai-icon
          name="external"
          :size="14"
        />
        {{ link.label }}
      </router-link>
    </footer>
    <p
      v-if="fileError"
      class="ai-evidence__error"
      role="alert"
    >
      {{ fileError }}
    </p>
  </article>
</template>

<script>
  import AiIcon from './AiIcon.vue';
  import { canOpen, fileRefs, openFile, openLabel } from './files';
  import { count, formatTime } from './format';
  import { recordLink, reportLink, streamLink } from './links';

  const ICON_SLUGS = [
    'clipboard',
    'computer',
    'email-clients',
    'external-drives',
    'file-upload',
    'files',
    'hardware-changes',
    'installed-programs',
    'keyboard',
    'messengers',
    'mic-records',
    'network-accesses',
    'network-files',
    'printer',
    'programs',
    'screenshots',
    'search-queries',
    'shadow-copyings',
    'social-networks',
    'visited-sites',
    'web-mail',
    'webcams-and-microphones',
  ];
  const ROW_LIMIT = 12;
  // What the text of a read file is.
  const TEXT_LABELS = { description: 'Что на изображении', transcript: 'Речь', text: 'Текст' };

  export default {
    name: 'EvidenceCard',
    components: { AiIcon },
    props: {
      item: { type: Object, required: true },
      number: { type: [Number, String], default: null },
      focused: { type: Boolean, default: false },
    },
    data: () => ({
      showFull: false,
      allRows: false,
      tableOpen: false,
      rowLimit: ROW_LIMIT,
      opening: null,
      fileError: '',
    }),
    computed: {
      card() {
        return this.item.card || {};
      },
      // A file the assistant read (its own evidence, derived from a record); other cards of kind
      // "file" are file events of the logs.
      isFile() {
        return this.item.category === 'file' || this.card.source === 'file';
      },
      // A file the user attached to the chat: it has no record in the logs, so no log links.
      isUpload() {
        return this.item.category === 'upload' || this.card.source === 'upload';
      },
      // A file the assistant read from a file of the user (an archive entry, a page…).
      fromUpload() {
        return this.card.parentSource === 'upload';
      },
      sourceLabel() {
        if (this.isUpload) return this.card.sourceLabel || 'Файл пользователя';
        return this.card.sourceLabel || 'Запись';
      },
      textLabel() {
        return this.isFile || this.isUpload ? TEXT_LABELS[this.card.textKind] || '' : '';
      },
      parentLabel() {
        if (this.fromUpload) return 'Из файла пользователя';
        return `Из записи${this.parentSource ? ` «${this.parentSource}»` : ''}`;
      },
      parentSource() {
        const slug = this.card.parentSource;
        const sources = (this.$store.state.assistant.capabilities || {}).sources || [];
        const found = sources.find((source) => source.slug === slug);
        return found ? found.label : '';
      },
      // Files the web interface can open: the read file itself or the files of the record.
      files() {
        if (this.isUpload) return [];
        const refs = this.isFile ? this.ownFile : fileRefs(this.item);
        return refs.map((ref) => ({ ...ref, label: openLabel(ref) }));
      },
      ownFile() {
        const { file } = this.card;
        if (!file || !file.kind) return [];
        const ref = { ...file, name: this.card.title || file.filename || 'Файл' };
        return canOpen(ref) ? [ref] : [];
      },
      fallbackIcon() {
        if (this.card.kind === 'report') return 'report';
        return this.isFile || this.isUpload ? 'file' : 'evidence';
      },
      iconUrl() {
        const slug = this.item.linkSource || this.card.source;
        if (!ICON_SLUGS.includes(slug)) return null;
        return require(`@/assets/images/icons/logs/category/${slug}.svg`);
      },
      time() {
        return formatTime(this.item.time || this.card.time);
      },
      flagLabel() {
        if (!this.card.flag) return '';
        return this.card.flag >= 2 ? 'Нарушение политики' : 'Внимание';
      },
      hasParties() {
        return !!(this.card.actor || this.card.counterpart || this.card.channel);
      },
      isLong() {
        return (this.card.text || '').length > 420;
      },
      columns() {
        return this.card.columns || [];
      },
      rows() {
        return this.card.rows || [];
      },
      visibleRows() {
        return this.allRows || this.tableOpen ? this.rows : this.rows.slice(0, ROW_LIMIT);
      },
      reportTitle() {
        return this.card.title || this.card.sourceLabel || 'Отчёт';
      },
      rowsLabel() {
        return count(this.rows.length, ['строка', 'строки', 'строк']);
      },
      links() {
        const result = [];
        if (this.isUpload) return result;
        const record = recordLink(this.item);
        if (record) {
          result.push({ label: 'Открыть в журнале', to: record, title: 'Журнал компьютера за день записи' });
        }
        const stream = streamLink(this.item);
        if (stream) {
          result.push({ label: 'Лента событий', to: stream, title: 'Все события компьютера за ±10 минут' });
        }
        const report = this.card.kind === 'report' ? reportLink(this.item) : null;
        if (report) result.push({ label: 'Открыть отчёт', to: report, title: 'Отчёт за тот же период' });
        return result;
      },
    },
    methods: {
      async open(file, index) {
        this.opening = index;
        this.fileError = '';
        try {
          this.fileError = await openFile(this.item.computerId, file);
        } catch (error) {
          this.fileError = 'Не удалось получить файл';
        } finally {
          this.opening = null;
        }
      },
      openTable() {
        this.tableOpen = true;
        // Focus inside the overlay: Esc is handled here and does not reach the chat window.
        this.$nextTick(() => this.$refs.report && this.$refs.report.focus());
      },
      closeTable() {
        this.tableOpen = false;
        this.$nextTick(() => this.$refs.expand && this.$refs.expand.focus());
      },
      onReportClick(event) {
        if (this.tableOpen && event.target === event.currentTarget) this.closeTable();
      },
      onReportEscape(event) {
        if (!this.tableOpen) return;
        event.stopPropagation();
        this.closeTable();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-evidence {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
    background: var(--ui-surface);
    box-shadow: var(--ui-shadow-sm);
    font-size: 13px;
    line-height: 1.45;
    scroll-margin: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &.is-focused {
      border-color: var(--ui-primary);
      box-shadow: var(--ui-focus);
    }
  }

  .ai-evidence__head {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }

  .ai-evidence__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 999px;
    background: var(--ui-primary-soft);
    color: var(--ui-primary);
    font-size: 12px;
    font-weight: 600;
  }

  .ai-evidence__source-icon {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    background-color: var(--ui-text-muted);
    -webkit-mask: var(--icon) center / contain no-repeat;
    mask: var(--icon) center / contain no-repeat;
  }

  .ai-evidence__fallback-icon {
    flex: 0 0 auto;
    color: var(--ui-text-muted);
  }

  .ai-evidence__source {
    color: var(--ui-text);
    font-weight: 500;
  }

  // Tinted by the shared .ai-tone (ChatWidget.vue).
  .ai-evidence__flag {
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
  }

  .ai-evidence__time {
    margin-left: auto;
    color: var(--ui-text-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  .ai-evidence__computer,
  .ai-evidence__parties {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    color: var(--ui-text-muted);
  }

  .ai-evidence__parties {
    color: var(--ui-text);
  }

  .ai-evidence__channel {
    padding: 0 6px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-bg-strong);
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-evidence__direction {
    display: inline-flex;
  }

  .is-in {
    color: var(--ui-success);
  }

  .is-out {
    color: var(--ui-primary);
  }

  .ai-evidence__title {
    color: var(--ui-text);
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  .ai-evidence__action {
    color: var(--ui-text-muted);
  }

  .ai-evidence__label {
    margin-bottom: -2px;
    color: var(--ui-text-muted);
    font-size: 12px;
    font-weight: 500;
  }

  .ai-evidence__parent {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  // An evidence id that opens that evidence in the panel.
  .ai-evidence__ref {
    padding: 1px 7px;
    border-radius: 999px !important;
    background: var(--ui-primary-soft);
    color: var(--ui-primary);
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: var(--ui-primary);
      color: #fff;
    }
  }

  .ai-evidence__error {
    margin: 0;
    color: var(--ui-danger);
    font-size: 12px;
  }

  .ai-evidence__text {
    margin: 0;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);
    color: var(--ui-text);
    white-space: pre-wrap;
    overflow-wrap: anywhere;

    &.is-clamped {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 7;
      -webkit-box-orient: vertical;
    }
  }

  .ai-evidence__files {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
  }

  .ai-evidence__file-name {
    overflow: hidden;
    color: var(--ui-text);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ai-evidence__muted {
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-evidence__fields {
    display: grid;
    grid-template-columns: minmax(80px, max-content) 1fr;
    gap: 3px 10px;
    margin: 0;

    dt {
      color: var(--ui-text-muted);
    }

    dd {
      margin: 0;
      color: var(--ui-text);
      overflow-wrap: anywhere;
    }
  }

  .ai-evidence__report-box {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .ai-evidence__table-wrap {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);
    // The theme's thin pale scrollbars are hard to notice: this one is the only hint of hidden columns.
    scrollbar-color: var(--ui-text-muted) var(--ui-bg-strong);
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      width: 12px;
      height: 12px;
    }

    &::-webkit-scrollbar-track {
      background: var(--ui-bg-strong);
    }

    &::-webkit-scrollbar-thumb {
      border: 3px solid var(--ui-bg-strong);
      border-radius: 999px;
      background: var(--ui-text-muted);
    }
  }

  // Columns take their content width up to the cell limit (longer values wrap), the table scrolls sideways.
  .ai-evidence__table {
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12px;

    th,
    td {
      padding: 4px 8px;
      border-bottom: 1px solid var(--ui-border);
      background: var(--ui-surface);
      text-align: left;
      vertical-align: top;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: var(--ui-surface-soft);
      color: var(--ui-text-muted);
      font-weight: 500;
    }

    // The first column (employee, computer…) stays in view while scrolling sideways.
    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
      box-shadow: inset -1px 0 0 var(--ui-border);
    }

    th:first-child {
      z-index: 2;
    }
  }

  .ai-evidence__cell {
    max-width: 220px;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  // In the narrow panel the sticky column must leave room for the scrolling ones.
  td:first-child .ai-evidence__cell,
  th:first-child .ai-evidence__cell {
    max-width: 140px;
  }

  .ai-evidence__table-actions,
  .ai-evidence__links {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .ai-evidence__links {
    padding-top: 4px;
  }

  // Expanded table: a fixed overlay above the page (and the chat window) with the whole table.
  .ai-evidence__report.is-open {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    cursor: default;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: var(--ui-text);
      opacity: 0.32;
    }

    .ai-evidence__report-box {
      position: relative;
      gap: 10px;
      max-width: 100%;
      max-height: 100%;
      padding: 12px 14px 14px;
      border-radius: var(--ui-radius-lg);
      outline: none;
      background: var(--ui-surface);
      box-shadow: var(--ui-shadow);
    }

    .ai-evidence__table-wrap {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }

    .ai-evidence__table {
      font-size: 13px;
    }

    .ai-evidence__cell {
      max-width: 220px;
    }
  }

  .ai-evidence__report-head {
    display: flex;
    align-items: center;
    gap: 10px;

    .ai-evidence__muted {
      white-space: nowrap;
    }
  }

  .ai-evidence__report-title {
    flex: 1 1 auto;
    min-width: 0;
    color: var(--ui-text);
    font-size: 14px;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  @media (max-width: 900px) {
    .ai-evidence__report.is-open {
      padding: 8px;
    }
  }
</style>
